import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { useApi } from '@/hooks/use-api';

// Per-token session caches so AppShell + Dashboard + Venture share one fetch.
let ventureCache = {};   // token -> venture|null
let scoreCache = {};     // token -> score payload
export const clearFounderCache = () => { ventureCache = {}; scoreCache = {}; };

/**
 * The current user's real, DB-stored venture (or null when they haven't
 * created one yet → caller shows the "Create your venture" onboarding).
 */
export const useVenture = () => {
  const { token } = useAuth();
  const api = useApi();
  const [venture, setVenture] = useState(() => (token && token in ventureCache ? ventureCache[token] : undefined));
  const [loading, setLoading] = useState(!(token && token in ventureCache));

  const refresh = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await api.get('/api/venture/me');
      ventureCache[token] = r.venture || null;
      setVenture(r.venture || null);
    } catch { setVenture(null); }
    finally { setLoading(false); }
  }, [token, api]);

  useEffect(() => {
    if (token && token in ventureCache) { setVenture(ventureCache[token]); setLoading(false); return; }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const saveVenture = useCallback(async (payload) => {
    const r = await api.post('/api/venture', payload);
    ventureCache[token] = r.venture || null;
    setVenture(r.venture || null);
    return r.venture;
  }, [api, token]);

  return { venture, loading, refresh, saveVenture };
};

/** Live Founder Score from real signals (0 for brand-new users). */
export const useFounderScore = () => {
  const { token } = useAuth();
  const api = useApi();
  const [score, setScore] = useState(() => (token && token in scoreCache ? scoreCache[token] : null));
  const [loading, setLoading] = useState(!(token && token in scoreCache));

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    if (token in scoreCache) { setScore(scoreCache[token]); setLoading(false); return; }
    let active = true;
    setLoading(true);
    api.get('/api/score/me')
      .then((d) => { scoreCache[token] = d; if (active) setScore(d); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return { score: score?.score ?? 0, delta: score?.delta ?? '', streak: score?.streak ?? 0, data: score, loading };
};

/** Real per-day founder activity (last 7 days) — replaces fabricated velocity. */
export const useActivity = () => {
  const { token } = useAuth();
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    let active = true;
    setLoading(true);
    api.get('/api/score/activity?days=7')
      .then((d) => { if (active) setData(d); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return { days: data?.days ?? [], totals: data?.totals ?? null, labels: data?.labels ?? {}, loading };
};
